// Lab workspace: topology panel + device consoles (+ optional physical-layer
// inspector and Windows adapter-settings dialog).

import { h } from './h.js';
import { renderTopology } from './topology.js';
import { createTerminal } from './terminal.js';
import { dev, isHost, linkState, hostIp } from '../engine/net/sim.js';
import { validateStatic } from '../engine/pc/winshell.js';
import { shortIf } from '../engine/ios/ifnames.js';

// Presentation-only view memory (per unit, this browser tab): which workspace
// view was last open. Never stored in lab state or progress.
const VIEW_MEMORY = new Map();

/**
 * The lab workspace. Two views share ONE topology renderer and ONE set of
 * terminals: switching views only toggles visibility, so console sessions,
 * scrollback, simulator state and evidence are untouched.
 *   view 'diagram' — the live topology (click a device to open its console)
 *   view 'cli'     — device tabs + the existing consoles
 * Options: onChange (lab state changed from the GUI), onExpand(bool) / onClose()
 * (layout controls owned by the unit page).
 */
export function createWorkspace(rt, { onChange, onExpand, onClose } = {}) {
  const unit = rt.unit;
  const memKey = `${rt.domainId}.${unit.id}`;
  const terms = {};
  let active = null;
  let view = VIEW_MEMORY.get(memKey) || 'diagram';
  let expanded = false;
  const diagram = h('div', { class: 'diagram' });
  const tabs = h('div', { class: 'term-tabs', role: 'tablist', 'aria-label': 'Device consoles' });
  const termHost = h('div', { class: 'term-host' });
  const tools = h('div', { class: 'ws-tools' });
  const legend = h('div', { class: 'diagram-legend' });

  const viewBtn = (v, label) => h('button', {
    class: 'ws-view', role: 'tab', id: `ws-view-${v}`, 'aria-controls': `ws-pane-${v}`, 'data-testid': `ws-view-${v}`,
    onclick: () => setView(v, true),
    onkeydown: (e) => { if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); const o = v === 'diagram' ? 'cli' : 'diagram'; setView(o, false); el.querySelector(`#ws-view-${o}`).focus(); } },
  }, label);
  const vDiagram = viewBtn('diagram', 'NETWORK DIAGRAM');
  const vCli = viewBtn('cli', 'CLI CONSOLE');
  const expandBtn = h('button', { class: 'btn small ghost ws-ctl ws-expand', 'data-testid': 'ws-expand', 'aria-pressed': 'false', onclick: () => setExpanded(!expanded) }, '⤢ Expand workspace');
  const closeBtn = h('button', { class: 'btn small ghost ws-ctl ws-close', 'data-testid': 'ws-close', 'aria-label': 'Close lab workspace and return to the question', onclick: () => { setExpanded(false); onClose?.(); } }, '✕ Close');
  const activeLabel = h('span', { class: 'ws-active' });
  const paneDiagram = h('div', { class: 'ws-pane ws-pane-diagram', role: 'tabpanel', id: 'ws-pane-diagram', 'aria-labelledby': 'ws-view-diagram', 'data-testid': 'ws-pane-diagram' },
    h('div', { class: 'panel-head' },
      h('span', { class: 'panel-title' }, 'NETWORK DIAGRAM'),
      h('span', { class: 'panel-hint' }, unit.linkLights ? 'Live link lights · click a device to open its console' : 'As-documented topology · click a device to open its console')),
    diagram, legend);
  const paneCli = h('div', { class: 'ws-pane ws-pane-cli', role: 'tabpanel', id: 'ws-pane-cli', 'aria-labelledby': 'ws-view-cli', 'data-testid': 'ws-pane-cli' },
    h('div', { class: 'panel-head' }, h('span', { class: 'panel-title' }, 'CONSOLE ACCESS'), activeLabel),
    tabs, termHost);

  const el = h('section', { class: 'workspace panel', 'aria-label': 'Lab workspace', 'data-testid': 'workspace' },
    h('div', { class: 'ws-bar' },
      h('span', { class: 'ws-title' }, 'LAB WORKSPACE'),
      h('div', { class: 'ws-views', role: 'tablist', 'aria-label': 'Workspace view' }, vDiagram, vCli),
      h('div', { class: 'ws-ctls' }, expandBtn, closeBtn)),
    paneDiagram, paneCli, tools);

  function setView(v, focusConsole) {
    view = v;
    VIEW_MEMORY.set(memKey, v);
    for (const [b, on] of [[vDiagram, v === 'diagram'], [vCli, v === 'cli']]) { b.setAttribute('aria-selected', on ? 'true' : 'false'); b.classList.toggle('on', on); b.tabIndex = on ? 0 : -1; }
    paneDiagram.hidden = v !== 'diagram';
    paneCli.hidden = v !== 'cli';
    el.dataset.view = v;
    if (v === 'cli' && active && terms[active]) { terms[active].refresh(); if (focusConsole) terms[active].focus(); }
  }
  function setExpanded(on) {
    expanded = on;
    expandBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
    expandBtn.textContent = on ? '⤡ Collapse workspace' : '⤢ Expand workspace';
    expandBtn.setAttribute('data-testid', on ? 'ws-collapse' : 'ws-expand');
    onExpand?.(on);
  }

  function devices() { return rt.consoleDevices(); }

  function drawDiagram() {
    const st = rt.stage;
    const layout = typeof unit.layout === 'function' ? unit.layout(rt) : unit.layout;
    diagram.textContent = '';
    diagram.append(renderTopology(layout, rt.net, {
      linkLights: unit.linkLights || st?.linkLights,
      consoleDevices: devices(), active,
      onNode: (id) => { activate(id, false); setView('cli', true); },
      onLink: unit.physical ? (l) => openCable(l) : null,
      ...(st?.diagramOpts ? st.diagramOpts(rt) : {}),
    }));
    diagram.querySelector('svg')?.setAttribute('preserveAspectRatio', 'xMidYMin meet'); // top-aligned when the pane is taller than the drawing
    legend.textContent = '';
    if (unit.linkLights || st?.linkLights) {
      legend.append(h('span', {}, h('i', { class: 'led-dot up' }), 'link up'), h('span', {}, h('i', { class: 'led-dot err' }), 'up with errors'), h('span', {}, h('i', { class: 'led-dot down' }), 'down'));
    }
    if (unit.physical) legend.append(h('span', { class: 'muted' }, 'Click a cable to inspect it physically.'));
  }

  function drawTabs() {
    tabs.textContent = '';
    for (const id of devices()) {
      const d = dev(rt.net, id);
      tabs.append(h('button', {
        class: `term-tab ${active === id ? 'on' : ''}`, role: 'tab', 'aria-selected': active === id ? 'true' : 'false', 'data-testid': `tab-${id}`,
        'aria-label': `${d.hostname} console (${isHost(d) ? 'Windows PowerShell' : 'Cisco IOS'})`,
        onclick: () => activate(id),
      }, h('span', { class: `kind kind-${d.kind}` }, isHost(d) ? 'PS' : 'IOS'), d.hostname, active === id ? h('span', { class: 'tab-mark', 'aria-hidden': 'true' }, '●') : null));
    }
    activeLabel.textContent = active ? `Active: ${dev(rt.net, active).hostname}` : '';
    if (!devices().length) tabs.append(h('div', { class: 'muted pad' }, 'No console access is needed for this step.'));
  }

  function activate(id, focus = true) {
    if (!devices().includes(id)) return;
    active = id;
    for (const t of Object.values(terms)) t.el.hidden = true;
    if (!terms[id]) { terms[id] = createTerminal(rt, id); termHost.append(terms[id].el); }
    terms[id].el.hidden = false;
    terms[id].refresh();
    drawTabs();
    drawDiagram();
    drawTools();
    if (focus && view === 'cli') terms[id].focus();
  }

  // ---------------------------------------------------------- Windows adapter GUI
  function drawTools() {
    tools.textContent = '';
    const d = active ? dev(rt.net, active) : null;
    if (d && isHost(d) && unit.adapterGui) {
      tools.append(h('button', { class: 'btn small ghost', 'data-testid': 'open-adapter', onclick: () => openAdapter(d.id) }, `⚙ ${d.hostname}: Ethernet adapter IPv4 settings`));
    }
  }
  function openAdapter(id) {
    const d = dev(rt.net, id);
    const c = d.ipcfg;
    const f = (name, val, label) => h('label', { class: 'fld' }, h('span', {}, label), h('input', { name, value: val || '', spellcheck: 'false', 'data-testid': `ad-${name}` }));
    const err = h('div', { class: 'form-err', role: 'alert' });
    const modeDhcp = h('input', { type: 'radio', name: 'mode', value: 'dhcp', checked: c.dhcp || null });
    const modeStatic = h('input', { type: 'radio', name: 'mode', value: 'static', checked: !c.dhcp || null });
    const form = h('form', { class: 'win-dialog', onsubmit: (e) => {
      e.preventDefault();
      const fd = new FormData(form);
      if (fd.get('mode') === 'dhcp') { rt.op('adapter', { device: id, dhcp: true }); close(); return; }
      const cfg = { ip: fd.get('ip').trim(), mask: fd.get('mask').trim(), gw: fd.get('gw').trim() || null, dns: fd.get('dns').trim() ? [fd.get('dns').trim()] : [] };
      const e2 = validateStatic(cfg);
      if (e2) { err.textContent = e2; return; }
      if (cfg.dns[0] && !/^\d{1,3}(\.\d{1,3}){3}$/.test(cfg.dns[0])) { err.textContent = 'The DNS server address is not valid.'; return; }
      rt.op('adapter', { device: id, ...cfg }); // src/core/labOps.js (replayed by the server)
      close();
    } },
    h('div', { class: 'win-title' }, 'Internet Protocol Version 4 (TCP/IPv4) Properties'),
    h('div', { class: 'win-body' },
      h('label', { class: 'radio' }, modeDhcp, 'Obtain an IP address automatically'),
      h('label', { class: 'radio' }, modeStatic, 'Use the following IP address:'),
      f('ip', c.dhcp ? '' : c.ip, 'IP address:'), f('mask', c.dhcp ? '' : c.mask, 'Subnet mask:'), f('gw', c.dhcp ? '' : c.gw, 'Default gateway:'),
      f('dns', c.dns[0], 'Preferred DNS server:'), err),
    h('div', { class: 'win-actions' }, h('button', { class: 'btn primary small', type: 'submit', 'data-testid': 'ad-ok' }, 'OK'), h('button', { class: 'btn ghost small', type: 'button', onclick: () => close() }, 'Cancel')));
    const wrap = h('div', { class: 'modal-wrap' }, form);
    const close = () => { wrap.remove(); refresh(); onChange?.(); };
    document.body.append(wrap);
    form.querySelector('input[name=ip]').focus();
  }

  // ---------------------------------------------------------- physical inspector
  function openCable(l) {
    const st = linkState(rt.net, l);
    const c = l.cable;
    const ends = [l.a, l.b].map((e) => `${dev(rt.net, e.dev).hostname} ${shortIf(e.ifc)}`);
    rt.action('inspect-cable', { link: l.id });
    const seen = [];
    seen.push(`Cable: ${c.type}, ${c.length} m, ${c.connector} connectors.`);
    if (!c.seated) seen.push('The RJ45 plug at the patch panel end is not clicked in — it slides out with no resistance.');
    else seen.push('Both connectors are fully seated.');
    if (c.damaged) seen.push('The jacket is crushed under a desk leg; two pairs look kinked.');
    if (c.length > 100) seen.push(`Length exceeds the 100 m TIA-568 channel limit for twisted pair.`);
    if (!c.compatible) seen.push('The connector type does not match this port.');
    const options = unit.physical.replacements || [];
    const body = h('div', { class: 'cable-panel panel', 'data-testid': 'cable-panel' },
      h('div', { class: 'panel-head' }, h('span', { class: 'panel-title' }, `PHYSICAL INSPECTION · ${ends.join(' ↔ ')}`), h('button', { class: 'btn small ghost', onclick: () => body.remove() }, 'Close')),
      h('ul', { class: 'obs' }, seen.map((s) => h('li', {}, s))),
      h('div', { class: 'muted small' }, 'Port LEDs: ', st === 'up' ? 'steady green' : st === 'errors' ? 'green, blinking amber' : 'off'),
      h('div', { class: 'cable-actions' },
        h('button', { class: 'btn small', 'data-testid': 'reseat', onclick: () => { rt.op('reseat', { link: l.id }); body.remove(); refresh(); onChange?.(); } }, 'Reseat both connectors'),
        options.length ? h('label', { class: 'fld inline' }, h('span', {}, 'Replace with:'),
          h('select', { 'data-testid': 'replace-select' }, h('option', { value: '' }, 'Choose from spares cabinet…'), options.map((o, i) => h('option', { value: i }, o.label)))) : null,
        options.length ? h('button', { class: 'btn small', 'data-testid': 'replace', onclick: (e) => {
          const sel = body.querySelector('select').value;
          if (sel === '') return;
          const o = options[+sel];
          if (o.fits === false) { e.target.textContent = 'Connector does not fit this port'; return; }
          rt.op('replace-cable', { link: l.id, option: o.id });
          body.remove(); refresh(); onChange?.();
        } }, 'Install') : null));
    tools.querySelector('.cable-panel')?.remove();
    tools.append(body);
  }

  function refresh() {
    drawDiagram();
    drawTabs();
    drawTools();
    if (active && terms[active]) terms[active].refresh();
  }

  function setDevices() {
    const list = devices();
    if (!list.includes(active)) active = null;
    drawTabs();
    drawDiagram();
    if (!active && list.length) activate(list[0], false);
  }

  setDevices();
  setView(view, false);
  return { el, refresh, setDevices, activate, terminal: (id) => terms[id], drawDiagram, setView, setExpanded, get view() { return view; }, get expanded() { return expanded; } };
}
export { hostIp };
